<template>
  <div class="gs-app gm-broadcast-analytics-refresh">
    <div class="flex mb-4 mx-2">
      <!-- Loader -->
      <portal to="loaderTarget">
        <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
          <gd-loader :label="$t('frontend_loading')"/>
        </div>
      </portal>

      <div class="w-full gm-broadcast-analytics-refresh__workspace">
        <navigation-tabs
          class="gm-nav-broadcast-analytics-tabs absolute"
          :nav_tabs="navTabs"
        ></navigation-tabs>
        <server-table
          class="w-full text-center groovemail-campaigns-analytics-table gs-refresh-controls"
          url="/campaign"
          ref="serverTable"
          tableRef="campaignsTable"
          :slotData="broadcastAnalyticsSlotData"
          :tableColumns="broadcastsTableColumns"
          :tableOptions="broadcastsTableOptions"
          :emptyStateData="emptyStateData"
          tableTitle="frontend_groovemail_broadcasts_analytics"
          @loaded="hideLoader()"
          @loading="showLoader()"
        >
          <template slot="top-right-content">
            <gd-dropdown
              class="text-left"
              size="full"
              v-model="percentSwitch"
              :options="dropdownOptions"
              @change="changeDataView"
            />
          </template>
        </server-table>
      </div>
    </div>

    <gs-named-modal class="gs-refresh-controls"
      id="subscribermodel"
      name="view-opens-modal"
      :title="$t('frontend_groovemail_broadcasts_unique_opens')"
    >
      <BroadcastOpens
        :opensUrl="opensUrl"
        @hideLoader="hideLoader()"
        @showLoader="showLoader()"
      >
      </BroadcastOpens>
    </gs-named-modal>

    <gs-named-modal class="gs-refresh-controls"
      id="subscribermodel"
      name="view-clicks-modal"
      :title="$t('frontend_groovemail_broadcasts_unique_clicks')"
    >
      <BroadcastClicks
        :clicksUrl="clicksUrl"
        @hideLoader="hideLoader()"
        @showLoader="showLoader()"
      >
      </BroadcastClicks>
    </gs-named-modal>

    <gs-named-modal class="gs-refresh-controls"
      id="failedLeadsModal"
      name="failed-leads-modal"
      :title="$t('frontend_groovemail_campaigns_failed_leads_tables_header')"
    >
      <BroadcastFailedEnteries
        :failedLeadsUrl="failedLeadsUrl"
        @hideLoader="hideLoader()"
        @showLoader="showLoader()"
      >
      </BroadcastFailedEnteries>
    </gs-named-modal>
  </div>
</template>

<script>
import axios from 'axios';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import { broadcastUrls } from '@/utils/urls.js';
import NavigationTabs from '@/components/navigation-tabs.vue';
import scheduledTimeInEst from '@/components/scheduledTime.js';
import BroadcastOpens from '@/components/broadcast-opens.vue';
import BroadcastClicks from '@/components/broadcast-clicks.vue';
import BroadcastFailedEnteries from '@/components/broadcast-failed-enteries.vue';

export default {
  name: 'BroadcastAnalytics',
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_broadcasts_analytics') };
  },
  data() {
    return {
      percentSwitch: false,
      isLoading: false,
      broadcastListUrls: broadcastUrls.broadcast,
      opensUrl: '',
      clicksUrl: '',
      failedLeadsUrl: '',
      campaigns: [],
      dropdownOptions: [
        {
          text: this.$t('frontend_groovemail_campaign_listing_show_data_as_percentage'),
          showValue: `${this.$t('frontend_groovemail_campaign_listing_show_data_as')} (%)`,
          value: true,
        },
        {
          text: this.$t('frontend_groovemail_campaign_listing_show_data_as_number'),
          showValue: `${this.$t('frontend_groovemail_campaign_listing_show_data_as')} (#)`,
          value: false,
        }
      ],
      broadcastsTableColumns: [
        'name',
        'unique_open_count',
        'unique_click_count',
        'unsubscribe_count',
        'total_recipients',
        'failed_recipients',
        'status',
        'actions',
      ],

      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_broadcasts_analytics',
        emptyStateSubHeading: 'frontend_groovemail_broadcasts_analytics_have_no_data',
      },
    };
  },

  components: {
    NavigationTabs,
    ServerTable,
    BroadcastOpens,
    BroadcastClicks,
    BroadcastFailedEnteries
  },

  methods: {
    // show loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader (){
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    /**
     * Returns scheduled time in EST format
     * @param {string} actualDate date recieved
     * @return {string} - time in EST
     */
    getScheduledTime(actualDate) {
      return scheduledTimeInEst(actualDate);
    },

    /**
     * Opens modal to display failed enteries corresponding to a broadcast
     * @param {string} campaignId broadcast Id recieved
     */
    async showFailedEnteries(campaignId) {
      this.failedLeadsUrl = `${broadcastUrls.failedRecipientList}?campaignId=${campaignId}`;
      this.$root.$emit('modal-open', 'failed-leads-modal');
    },

    /**
     * Opens modal to display unique open enteries corresponding to a broadcast
     * @param {string} campaignId broadcast Id recieved
     */
    async showUniqueOpens(campaignId) {
      this.opensUrl = `${broadcastUrls.openedBroadcasts}?campaignId=${campaignId}`;
      this.$root.$emit('modal-open', 'view-opens-modal');
    },

    /**
     * Opens modal to display unique click enteries corresponding to a broadcast
     * @param {string} campaignId broadcast Id recieved
     */
    async showUniqueClicks(campaignId) {
      this.clicksUrl = `${broadcastUrls.clickedBroadcastLinks}?campaignId=${campaignId}`;
      this.$root.$emit('modal-open', 'view-clicks-modal');
    },

    /**
     * Redirects to detail page
     * @param {string} Id broadcast Id
     */
    showcampaign(Id) {
      this.$router.push({
        name: 'CampaignDetail',
        params: { id: Id },
        query: { from: 'broadcast-analytics-listing' },
      });
    },

    /**
     * Opens up closem url for adding postal details for broadcast
     * @param {object} postalData - postal broadcast data
     */
    showStatisticsForPostal(postalData) {
      window.open(
        `https://app.closem.ai/index.php?m=campaigns&d=campaigndetails&id=${postalData.postal_campaign_id}`
      );
    },

    /**
     * shows the data as number or percentage based on the boolean value
     * @param {boolean} value - if true shows percent values, otherwise numbers
     */
    changeDataView(value) {
      this.percentSwitch = value;
    },
  },

  computed: {
    /**
     * Returns navigation tabs data for broadcast analytics page
     * @return {array} navigation tabs data
     */
     navTabs() {
      return [
          {
            tabName: 'frontend_groovemail_leads_route_page_title',
            tabRoute: 'LeadAnalytics',
          },
          {
            tabName: 'frontend_groovemail_lists_route_page_title',
            tabRoute: 'ListAnalytics',
          },
          {
            tabName: 'frontend_groovemail_broadcasts_route_page_title',
            tabRoute: 'BroadcastAnalytics',
          },
          {
            tabName: 'frontend_groovemail_sequences_route_page_title',
            tabRoute: 'SequenceAnalytics',
          },
        ];
    },

    /**
     * Returns table options for broadcast analytics table
     * @return {object} table options for broadcast analytics table
    */
    broadcastsTableOptions() {
      return {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [
          'name',
          'total_recipients',
          'unique_open_count',
          'unique_click_count',
        ],
        headings: {
          name: () => this.$t('frontend_groovemail_campaign_name'),
          unique_open_count: () => this.$t('frontend_groovemail_campains_show_opens'),
          unique_click_count: () => this.$t('frontend_groovemail_campains_show_clicks'),
          unsubscribe_count: () => this.$t('frontend_groovemail_admin_campaign_unsubscibers_short'),
          total_recipients: () => this.$t('frontend_groovemail_campains_show_recipients'),
          failed_recipients: () => this.$t('frontend_groovemail_contacts_step3_failed'),
          status: () => this.$t('frontend_groovemail_campains_show_status'),
          actions: () => this.$t('frontend_groovemail_campains_index_actions'),
        },
        filterByColumn: false,
        filterable: ['name'],
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        texts: {
          noResults: this.$t('frontend_groovemail_broadcasts_no_campaigns_created'),
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        pagination: { chunk: 5 },
        requestFunction(data) {
          let paramsData = data;
          if (!data.orderBy) {
            paramsData = { ...data, ascending: 0 };
          }
          return axios
            .get(this.url, {
              params: paramsData,
            })
            .catch((e) => {
              this.dispatch('error', e);
            });
        },
        responseAdapter({ data }) {
          return {
            data: data.data.data ? data.data.data : [],
            count: data.data.total ? data.data.total : 0,
          };
        },
      };
    },

    /**
     * Returns navigation tabs data for broadcast analytics page
     * @return {array} navigation tabs data
     */
     navTabs() {
      return [
          {
            tabName: 'frontend_groovemail_leads_route_page_title',
            tabRoute: 'LeadAnalytics',
          },
          {
            tabName: 'frontend_groovemail_lists_route_page_title',
            tabRoute: 'ListAnalytics',
          },
          {
            tabName: 'frontend_groovemail_broadcasts_route_page_title',
            tabRoute: 'BroadcastAnalytics',
          },
          {
            tabName: 'frontend_groovemail_sequences_route_page_title',
            tabRoute: 'SequenceAnalytics',
          },
        ];
    },

    /**
     * Returns table options for broadcast analytics table
     * @return {object} table options for broadcast analytics table
    */
    broadcastsTableOptions() {
      return {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [
          'name',
          'total_recipients',
          'unique_open_count',
          'unique_click_count',
        ],
        headings: {
          name: () => this.$t('frontend_groovemail_campaign_name'),
          unique_open_count: () => this.$t('frontend_groovemail_campains_show_opens'),
          unique_click_count: () => this.$t('frontend_groovemail_campains_show_clicks'),
          unsubscribe_count: () => this.$t('frontend_groovemail_admin_campaign_unsubscibers_short'),
          total_recipients: () => this.$t('frontend_groovemail_campains_show_recipients'),
          failed_recipients: () => this.$t('frontend_groovemail_contacts_step3_failed'),
          status: () => this.$t('frontend_groovemail_campains_show_status'),
          actions: () => this.$t('frontend_groovemail_campains_index_actions'),
        },
        filterByColumn: false,
        filterable: ['name'],
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        texts: {
          noResults: this.$t('frontend_groovemail_broadcasts_no_campaigns_created'),
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        pagination: { chunk: 5 },
        requestFunction(data) {
          let paramsData = data;
          if (!data.orderBy) {
            paramsData = { ...data, ascending: 0 };
          }
          return axios
            .get(this.url, {
              params: paramsData,
            })
            .catch((e) => {
              this.dispatch('error', e);
            });
        },
        responseAdapter({ data }) {
          return {
            data: data.data.data ? data.data.data : [],
            count: data.data.total ? data.data.total : 0,
          };
        },
      };
    },

    /**	
     * Returns slot data for broadcast analytics table.	
     * @return {array} details for slot columns for broadcast analytics table.	
    */
    broadcastAnalyticsSlotData() {
      return [
        {
          slotName: 'name',
          slotType: 'icon',
          icon: (props) => {
            if (props.row.method === 'email') return 'campaign-email-icon';
            if (props.row.method === 'sms') return 'campaign-sms-icon';

            return 'campaign-postal-icon';
          },
          conditions: () => true,
          classes: 'pointer-events-none',
          description: {
            show: true,
            callback: (props) => this.showcampaign(props.row.id),
            html: (props) => `<span class="hover:underline cursor-pointer">${props.row.name}</span>`,
          },
        },
        {
          slotName: 'unique_open_count',
          slotType: 'default',
          conditions: () => true,
          classes: '',
          callback: (props) => {
            if (props.row.method === 'postal' && props.row.postal_campaign_id)
              this.showStatisticsForPostal(props.row);
            else if (!this.percentSwitch && props.row.unique_open_count) {
              this.showUniqueOpens(props.row.id);
            } else if (
              this.percentSwitch &&
              props.row.unique_percent_open_rate
            ) {
              this.showUniqueOpens(props.row.id);
            }
          },
          html: (props) => {
            let result;
            if (props.row.method === 'postal' && props.row.postal_campaign_id) {
              result = `<span style="margin-top: -10px;">${this.$t(
                'frontend_groovemail_campaigns_create_postal_integration_postal_click_here_stats'
              )}</span>`;
            } else if (!this.percentSwitch) {
              result = props.row.unique_open_count
                ? `<span class="cursor-pointer font-bold">${props.row.unique_open_count}</span>`
                : `<span>0</span>`;
            } else {
              result = props.row.unique_percent_open_rate
                ? `<span class="cursor-pointer font-bold">${props.row.unique_percent_open_rate}%</span>`
                : `<span>0%</span>`;
            }
            return result;
          },
        },
        {
          slotName: 'unique_click_count',
          slotType: 'default',
          conditions: (props) => !(props.row.method === 'postal' && props.row.postal_campaign_id),
          classes: '',
          callback: (props) => {
            if (
              (!this.percentSwitch && props.row.unique_click_count) ||
              (this.percentSwitch && props.row.unique_percent_click_rate)
            )
              this.showUniqueClicks(props.row.id);
          },
          html: (props) => {
            let result;
            if (!this.percentSwitch) {
              result = props.row.unique_click_count
                ? `<span class="cursor-pointer font-bold">${props.row.unique_click_count}</span>`
                : `<span>0</span>`;
            } else {
              result = props.row.unique_percent_click_rate
                ? `<span class="cursor-pointer font-bold">${props.row.unique_percent_click_rate}%</span>`
                : `<span>0%</span>`;
            }
            return result;
          },
        },
        {
          slotName: 'unsubscribe_count',
          slotType: 'computed',
          conditions: (props) => !(props.row.method === 'postal' && props.row.postal_campaign_id),
          classes: () => '',
          value: (props) => {
            let result;
            if (!this.percentSwitch) {
              result = props.row.unsubscribe_count || 0;
            } else {
              result = `${props.row.unsubscribe_percent || 0}%`;
            }
            return result;
          },
        },
        {
          slotName: 'total_recipients',
          slotType: 'computed',
          classes: () => '',
          conditions: () => true,
          value: (props) => props.row.total_recipients ? props.row.total_recipients : 0,
        },
        {
          slotName: 'failed_recipients',
          slotType: 'default',
          conditions: () => true,
          classes: '',
          callback: (props) => {
            if (props.row.method === 'email' && props.row.failed_recipients) {
              this.showFailedEnteries(props.row.id);
            }
          },
          html: (props) => {
            let result;
            if (props.row.method === 'postal') {
              result = `<span>${props.row.failed_recipients || 0}</span>`;
            } else if (props.row.method === 'email') {
              result = props.row.failed_recipients
                ? `<span class="cursor-pointer font-bold">${props.row.failed_recipients}</span>`
                : `<span>0</span>`;
            } else {
              result = `<span>${props.row.failed_recipients || 0}</span>`;
            }
            return result;
          },
        },
        {
          slotName: 'status',
          slotType: 'status',
          classes: (props) => {
            if (props.row.is_completed === 1)
              return 'border-success text-success';
            if (props.row.is_completed === 2)
              return 'border-danger text-danger';
            if (props.row.emailscheduled === 'sendlater')
              return 'border-info text-info';
            if (props.row.is_stop === 1)
              return 'border-danger text-danger';
            if (props.row.emailscheduled === 'sendnow')
              return 'border-purple text-purple';
            if (props.row.emailscheduled === 'savedraft')
              return 'border-warning text-warning';

            return '';
          },
          statusTitle: (props) => {
            if (props.row.is_completed === 1)
              return this.$t('frontend_groovemail_campains_index_sent');
            if (props.row.is_completed === 2)
              return this.$t('frontend_groovemail_contacts_step3_failed');
            if (props.row.emailscheduled === 'sendlater')
              return this.$t('frontend_groovemail_campains_index_schedule');
            if (props.row.is_stop === 1)
              return this.$t('frontend_groovemail_campains_index_stopped');
            if (props.row.emailscheduled === 'sendnow')
              return this.$t('frontend_groovemail_campains_index_inprocess');
            if (props.row.emailscheduled === 'savedraft')
              return this.$t('frontend_groovemail_campains_index_draft');

            return '';
          },
          conditions: (props) => {
            if (
              !props.row.is_completed &&
              (props.row.is_stop === 1 ||
                props.row.emailscheduled === 'sendnow' ||
                (props.row.emailscheduled === 'savedraft' &&
                  !props.row.updated_at))
            )
              return false;
            return true;
          },
          value: (props) => {
            if (props.row.is_completed === 1)
              return props.row.broadcastActualSendTime
                ? this.getScheduledTime(props.row.broadcastActualSendTime)
                : this.$t('frontend_affiliate_index_NA');
            if (props.row.is_completed === 2)
              return props.row.broadcastActualSendTime
                ? this.getScheduledTime(props.row.broadcastActualSendTime)
                : this.$t('frontend_affiliate_index_NA');
            if (props.row.emailscheduled === 'sendlater')
              return props.row.actualdate
                ? this.getScheduledTime(props.row.actualdate)
                : this.$t('frontend_affiliate_index_NA');
            if (props.row.emailscheduled === 'savedraft')
              return `${this.$t(
                'frontend_groovemail_campains_index_last_edited'
              )} ${parseInt(Math.abs(new Date() - new Date(props.row.updated_at)) / 864e5, 10
              )}d ${this.$t('frontend_groovemail_campains_index_ago')}`;
          },
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => true,
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_groovemail_analytics_broadcast_detailed'),
              conditions: () => true,
              callback: (props) => this.showcampaign(props.row.id),
            },
          ],
        },
        {
          slotName: 'view',
          slotType: 'icon',
          conditions: (props) => (
              props.row.is_processing === 1 ||
              props.row.is_completed === 1 ||
              props.row.is_completed === 2 ||
              props.row.is_stop === 1
            ),
          classes: 'expandIcon cursor-pointer w-4',
          callback: (props) => this.showcampaign(props.row.id),
          icon: () => 'snapshot',
          description: {
            show: false,
          },
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.groovemail-campaigns-analytics-table {
  .VueTables--server .table-responsive {
    th:nth-child(1),
    td:nth-child(1) {
      width: 22%;
      padding-left: 20px !important;
    }
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4) {
      width: 9%;
    }
    th:nth-child(5),
    td:nth-child(5) {
      width: 10%;
    }
    th:nth-child(6),
    td:nth-child(6) {
      width: 8%;
    }
    th:nth-child(7),
    td:nth-child(7) {
      width: 23%;
      padding-left: 5px !important;
      padding-right: 5px !important;
    }
    th:nth-child(8),
    td:nth-child(8) {
      width: 10%;
      padding-left: 5px !important;
      padding-right: 5px !important;
    }
  }
}

.gs-default-tabs-item:focus {
  outline: none !important;
  outline-style: none !important;
}

.gm-nav-broadcast-analytics-tabs {
  top: 37px;
  z-index: 10;
}
</style>

<style scoped>
.gm-broadcast-analytics-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-broadcast-analytics-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-broadcast-analytics-refresh >>> .VueTables__table,
.gm-broadcast-analytics-refresh >>> .VueTables__table th,
.gm-broadcast-analytics-refresh >>> .VueTables__table td {
  border-color: var(--gs-border);
}

@media (max-width: 640px) {
  .gm-broadcast-analytics-refresh { padding: 18px 12px 32px; }
  .gm-broadcast-analytics-refresh__workspace { padding: 16px; }
}
</style>
